// src/CategoryManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

function CategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [categoryName, setCategoryName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);

  // --- (เพิ่ม 1) States สำหรับ Modal ยืนยันการลบ ---
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null); // (เก็บ {id, name})

  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (err) {
      toast.error("ไม่สามารถโหลดหมวดหมู่ได้");
    } finally {
      setIsLoading(false);
    }
  };

  // (แก้ ESLint Warning)
  useEffect(() => {
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  const clearForm = () => {
    setCategoryName('');
    setEditingCatId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryName) {
      toast.error("กรุณากรอกชื่อหมวดหมู่");
      return;
    }
    const payload = { name: categoryName };
    try {
      if (editingCatId) {
        const response = await axios.put(`${API_URL}/categories/${editingCatId}`, payload, getAuthHeaders());
        toast.success(response.data.msg);
      } else {
        const response = await axios.post(`${API_URL}/categories`, payload, getAuthHeaders());
        toast.success(response.data.msg);
      }
      clearForm();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };

  const handleEditClick = (cat) => {
    setEditingCatId(cat.id);
    setCategoryName(cat.name);
    window.scrollTo(0, 0);
  };

  // --- (อัปเกรด 2) เปลี่ยน handleDeleteClick เป็น "เปิด Modal" ---
  const handleOpenDeleteModal = (cat) => {
    // if (!window.confirm(...)) // (ลบอันนี้)
    setDeletingCategory(cat);
    setIsDeleteModalOpen(true);
  };

  // --- (เพิ่ม 3) ฟังก์ชัน "ยืนยัน" การลบ (ใน Modal) ---
  const handleConfirmDelete = async () => {
    if (!deletingCategory) return;
    try {
      const response = await axios.delete(`${API_URL}/categories/${deletingCategory.id}`, getAuthHeaders());
      toast.success(response.data.msg);
      fetchCategories();
    } catch (err) {
      // (Backend จะตอบ 409 Conflict ถ้าลบไม่ได้)
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingCategory(null);
    }
  };

  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <button
        onClick={() => navigate('/admin/documents')}
        className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-300"
      >
        &larr; ย้อนกลับ
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* --- ส่วนที่ 1: ฟอร์มสร้าง/แก้ไข --- */}
        <div className="lg:col-span-1">
          <div className="w-full bg-white rounded-lg shadow-xl p-8 sticky top-28">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
              {editingCatId ? 'แก้ไขหมวดหมู่' : 'สร้างหมวดหมู่ใหม่'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="catName" className="block text-base font-medium text-gray-700">ชื่อหมวดหมู่ (Category Name) *</label>
                <input
                  type="text" id="catName" value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <button
                  type="submit"
                  className={`w-full px-4 py-3 font-semibold text-xl text-white rounded-md transition duration-300 ${
                    editingCatId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {editingCatId ? 'บันทึกการแก้ไข' : 'สร้างหมวดหมู่'}
                </button>
                {editingCatId && (
                  <button
                    type="button"
                    onClick={clearForm}
                    className="w-full mt-3 px-4 py-2 font-semibold text-base text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    ยกเลิกการแก้ไข
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* --- ส่วนที่ 2: ตารางแสดงหมวดหมู่ --- */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-3">
            หมวดหมู่ที่มีอยู่
          </h2>
          <div className="overflow-x-auto">
            {isLoading ? <p>Loading categories...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50">
                  <tr>
                    {/* --- (แก้ 1) เปลี่ยน ID เป็น ลำดับที่ --- */}
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase w-20">No.</th>
                    <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  
                  {/* --- (แก้ 2) เพิ่ม (cat, index) --- */}
                  {categories.map((cat, index) => (
                    <tr key={cat.id} className="hover:bg-gray-50">
                      
                      {/* (แสดงลำดับที่ โดยเริ่มจาก 1) */}
                      <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-700">{index + 1}</td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-900 font-medium">{cat.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-lg font-medium space-x-4">
                        <button
                          onClick={() => handleEditClick(cat)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          แก้ไข
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(cat)}
                            className="text-red-600 hover:text-red-900"
                        >
                          ลบ
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* --- (สิ้นสุดการแก้ 2) --- */}
                  {isDeleteModalOpen && deletingCategory && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                        <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
                            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
                                ยืนยันการลบหมวดหมู่
                            </h2>
                            <p className="mb-6 text-lg">
                                คุณแน่ใจหรือไม่ว่าต้องการลบหมวดหมู่: <br/>
                                <strong className="text-red-600">{deletingCategory.name}</strong>?
                            </p>
                            <p className="text-base text-gray-600">
                                (หมายเหตุ: จะลบได้ต่อเมื่อไม่มีเอกสารใดใช้หมวดหมู่นี้อยู่)
                            </p>
                            <div className="flex justify-end space-x-3 pt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
                                    >
                                        ยกเลิก
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmDelete} // (เรียกฟังก์ชันลบจริง)
                                        className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
                                >
                                    ยืนยันการลบ
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>

    
  );
}

export default CategoryManagement;